import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../ui';
import { ArrowUpDown, Filter, ChevronLeft, ChevronRight } from 'lucide-react';
import { WalletTransaction, walletService, TransactionType } from '../../services/wallet.service';

interface TransactionHistoryProps {
  onTransactionClick?: (transaction: WalletTransaction) => void;
}

const TRANSACTION_TYPE_COLORS: Record<TransactionType, string> = {
  [TransactionType.CREDIT]: 'text-green-600 bg-green-50 dark:bg-green-950 dark:text-green-400',
  [TransactionType.DEBIT]: 'text-red-600 bg-red-50 dark:bg-red-950 dark:text-red-400',
  [TransactionType.REFUND]: 'text-blue-600 bg-blue-50 dark:bg-blue-950 dark:text-blue-400',
  [TransactionType.ADJUSTMENT]: 'text-purple-600 bg-purple-50 dark:bg-purple-950 dark:text-purple-400',
  [TransactionType.DEPOSIT]: 'text-green-600 bg-green-50 dark:bg-green-950 dark:text-green-400',
  [TransactionType.WITHDRAWAL]: 'text-orange-600 bg-orange-50 dark:bg-orange-950 dark:text-orange-400',
  [TransactionType.ACTIVATION_FEE]: 'text-emerald-700 bg-emerald-50 dark:bg-emerald-950 dark:text-emerald-400',
};

const PAYMENT_STATUS_COLORS: Record<string, string> = {
  'CREATED': 'text-gray-600 bg-gray-50 dark:bg-gray-950 dark:text-gray-400',
  'PROCEED': 'text-blue-600 bg-blue-50 dark:bg-blue-950 dark:text-blue-400',
  'PENDING': 'text-yellow-600 bg-yellow-50 dark:bg-yellow-950 dark:text-yellow-400',
  'PAID': 'text-green-600 bg-green-50 dark:bg-green-950 dark:text-green-400',
  'FAILED': 'text-red-600 bg-red-50 dark:bg-red-950 dark:text-red-400',
};

const PAYMENT_STATUS_LABELS: Record<string, string> = {
  'CREATED': 'Created',
  'PROCEED': 'Processing',
  'PENDING': 'Pending',
  'PAID': 'Paid',
  'FAILED': 'Failed',
};

const TRANSACTION_TYPE_ICONS: Record<TransactionType, string> = {
  [TransactionType.CREDIT]: '↓',
  [TransactionType.DEBIT]: '↑',
  [TransactionType.REFUND]: '↺',
  [TransactionType.ADJUSTMENT]: '≡',
  [TransactionType.DEPOSIT]: '↓',
  [TransactionType.WITHDRAWAL]: '↑',
  [TransactionType.ACTIVATION_FEE]: '★',
};

export function TransactionHistory({ onTransactionClick }: TransactionHistoryProps) {
  const [transactions, setTransactions] = useState<WalletTransaction[]>([]);
  const [loading, setLoading] = useState(true);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [filterType, setFilterType] = useState<TransactionType | ''>('');
  const limit = 10;

  const loadTransactions = async () => {
    setLoading(true);
    try {
      const response = await walletService.getTransactions({
        page: currentPage,
        limit,
        type: filterType || undefined,
      });
      setTransactions(response.transactions);
      setTotalPages(response.pagination.totalPages);
    } catch (error) {
      console.error('Failed to load transactions:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadTransactions();
  }, [currentPage, filterType]);

  const formatAmount = (transaction: WalletTransaction) => {
    // SGD-priced fees/deposits carry a snapshot of what the user actually
    // paid in their display currency — prefer it over the MYR ledger amount.
    const meta = (transaction.metadata ?? {}) as Record<string, any>;
    const amountLabel =
      meta.displayCurrency && meta.displayAmount !== undefined
        ? `${Number(meta.displayAmount).toFixed(2)} ${meta.displayCurrency}`
        : `${transaction.amount.toFixed(2)} ${transaction.currency}`;

    // For deposits/activation fees, only show positive amount if payment status is PAID
    if ([TransactionType.DEPOSIT, TransactionType.ACTIVATION_FEE].includes(transaction.type as TransactionType) && transaction.paymentStatus !== 'PAID') {
      return amountLabel;
    }

    const isPositive = [TransactionType.CREDIT, TransactionType.DEPOSIT, TransactionType.REFUND].includes(transaction.type as TransactionType);
    const sign = isPositive ? '+' : '-';
    return `${sign}${amountLabel}`;
  };

  const getAmountColor = (transaction: WalletTransaction) => {
    // For deposits/activation fees, color depends on payment status
    if ([TransactionType.DEPOSIT, TransactionType.ACTIVATION_FEE].includes(transaction.type as TransactionType)) {
      if (transaction.paymentStatus === 'PAID') {
        return 'text-green-600 dark:text-green-400';
      } else if (transaction.paymentStatus === 'FAILED') {
        return 'text-red-600 dark:text-red-400';
      } else {
        return 'text-gray-600 dark:text-gray-400';
      }
    }

    return [TransactionType.CREDIT, TransactionType.DEPOSIT, TransactionType.REFUND].includes(transaction.type as TransactionType)
      ? 'text-green-600 dark:text-green-400'
      : 'text-red-600 dark:text-red-400';
  };

  const getTransactionColor = (transaction: WalletTransaction) => {
    // For deposits/activation fees with payment status, use payment status color
    if ([TransactionType.DEPOSIT, TransactionType.ACTIVATION_FEE].includes(transaction.type as TransactionType) && transaction.paymentStatus) {
      return PAYMENT_STATUS_COLORS[transaction.paymentStatus] || TRANSACTION_TYPE_COLORS[transaction.type as TransactionType];
    }
    return TRANSACTION_TYPE_COLORS[transaction.type as TransactionType];
  };

  return (
    <Card>
      <CardHeader>
        <div className="flex items-center justify-between">
          <CardTitle className="flex items-center gap-2">
            <ArrowUpDown className="h-5 w-5" />
            Transaction History
          </CardTitle>
          <div className="flex items-center gap-2">
            <Filter className="h-4 w-4 text-gray-500" />
            <select
              value={filterType}
              onChange={(e) => {
                setFilterType(e.target.value as TransactionType | '');
                setCurrentPage(1);
              }}
              className="px-3 py-1.5 rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              <option value="">All Types</option>
              <option value={TransactionType.CREDIT}>Credit</option>
              <option value={TransactionType.DEBIT}>Debit</option>
              <option value={TransactionType.REFUND}>Refund</option>
              <option value={TransactionType.DEPOSIT}>Deposit</option>
              <option value={TransactionType.WITHDRAWAL}>Withdrawal</option>
              <option value={TransactionType.ADJUSTMENT}>Adjustment</option>
              <option value={TransactionType.ACTIVATION_FEE}>Activation Fee</option>
            </select>
          </div>
        </div>
      </CardHeader>
      <CardContent>
        {loading ? (
          <div className="flex justify-center py-8">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
          </div>
        ) : transactions.length === 0 ? (
          <div className="text-center py-8 text-gray-500 dark:text-gray-400">
            <p>No transactions found</p>
          </div>
        ) : (
          <div className="space-y-3">
            {transactions.map((transaction) => (
              <div
                key={transaction.id}
                onClick={() => onTransactionClick?.(transaction)}
                className="flex items-center justify-between p-4 bg-gray-50 dark:bg-gray-800 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors cursor-pointer"
              >
                <div className="flex items-center gap-3">
                  <div className={`w-10 h-10 rounded-full flex items-center justify-center text-lg font-semibold ${
                    getTransactionColor(transaction)
                  }`}>
                    {TRANSACTION_TYPE_ICONS[transaction.type as TransactionType]}
                  </div>
                  <div>
                    <div className="flex items-center gap-2">
                      <p className="font-medium text-gray-900 dark:text-white">{transaction.description}</p>
                      {[TransactionType.DEPOSIT, TransactionType.ACTIVATION_FEE].includes(transaction.type as TransactionType) && transaction.paymentStatus && (
                        <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${
                          PAYMENT_STATUS_COLORS[transaction.paymentStatus]
                        }`}>
                          {PAYMENT_STATUS_LABELS[transaction.paymentStatus]}
                        </span>
                      )}
                    </div>
                    <p className="text-sm text-gray-500 dark:text-gray-400">
                      {new Date(transaction.createdAt).toLocaleDateString()} at {new Date(transaction.createdAt).toLocaleTimeString()}
                    </p>
                  </div>
                </div>
                <div className="text-right">
                  <p className={`text-lg font-semibold ${getAmountColor(transaction)}`}>
                    {formatAmount(transaction)}
                  </p>
                  <p className="text-xs text-gray-500 dark:text-gray-400">
                    Balance: {transaction.balanceAfter.toFixed(2)} {transaction.currency}
                  </p>
                </div>
              </div>
            ))}
          </div>
        )}

        {/* Pagination */}
        {totalPages > 1 && (
          <div className="flex items-center justify-between mt-4 pt-4 border-t border-gray-200 dark:border-gray-700">
            <button
              onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
              disabled={currentPage === 1}
              className="flex items-center gap-2 px-4 py-2 rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors"
            >
              <ChevronLeft className="h-4 w-4" />
              Previous
            </button>
            <p className="text-sm text-gray-600 dark:text-gray-400">
              Page {currentPage} of {totalPages}
            </p>
            <button
              onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))}
              disabled={currentPage === totalPages}
              className="flex items-center gap-2 px-4 py-2 rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors"
            >
              Next
              <ChevronRight className="h-4 w-4" />
            </button>
          </div>
        )}
      </CardContent>
    </Card>
  );
}
